/**
 * Visual Stage — Stage 4: Visual aid validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.5
 *
 * Validates:
 * - Required families have visual aids
 * - stimulusAids/supportAids follow semantic convention
 * - Visual types match policy-allowed types
 * - No answer leakage in visual data
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { blocker, coach, info } from '../issueContract'
import { getVisualPolicy, familyRequiresVisual } from '../policyEngine'
import type { Question } from '../../../content'

const STAGE: PipelineStage = 4

/**
 * Run visual validation on a question.
 */
export function runVisualStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  if (!question.familyId) return issues

  const visualPolicy = getVisualPolicy(question.familyId)
  const isRequired = familyRequiresVisual(question.familyId)

  // ── Required visual check ────────────────────────────────────────────────

  if (isRequired && visualPolicy) {
    // Stimulus required check
    if (visualPolicy.stimulusRequired) {
      const hasStimulus =
        Array.isArray(question.stimulusAids) && question.stimulusAids.length > 0

      // Exception: ordering-type questions within stimulus-required families
      // naturally don't have a single stimulus (e.g., "order these 4 times"
      // in a clock family — there's no single clock to display).
      const isOrderingException =
        question.questionType === 'ordering' && !hasStimulus

      if (!hasStimulus && !isOrderingException) {
        issues.push(
          blocker(
            'visual',
            STAGE,
            'VISUAL_STIMULUS_REQUIRED',
            `Family "${question.familyId}" requires a stimulus visual aid`,
            {
              field: 'stimulusAids',
              questionId: qId,
              familyId: fId,
              suggestion: visualPolicy.stimulusDefault
                ? `Add a "${visualPolicy.stimulusDefault}" visual to stimulusAids`
                : 'Add an appropriate visual to stimulusAids',
            },
          ),
        )
      } else if (hasStimulus && visualPolicy.stimulusDefault) {
        // GAP-P5: Verify stimulus visual type matches governance policy default
        for (let i = 0; i < question.stimulusAids.length; i++) {
          const aid = question.stimulusAids[i] as
            | Record<string, unknown>
            | undefined
          if (aid && typeof aid === 'object' && 'type' in aid) {
            const aidType = aid.type as string
            if (aidType !== visualPolicy.stimulusDefault) {
              issues.push(
                coach(
                  'visual',
                  STAGE,
                  'VISUAL_STIMULUS_TYPE_MISMATCH',
                  `Stimulus visual type "${aidType}" does not match governance default "${visualPolicy.stimulusDefault}" for family "${question.familyId}"`,
                  {
                    field: `stimulusAids[${i}].type`,
                    expected: visualPolicy.stimulusDefault,
                    actual: aidType,
                    questionId: qId,
                    familyId: fId,
                    severity: 35,
                    suggestion: `Consider using "${visualPolicy.stimulusDefault}" as the stimulus type, or update the governance map if "${aidType}" is intentional`,
                  },
                ),
              )
            }
          }
        }
      }
    }

    // Support required check
    if (visualPolicy.supportRequired) {
      const hasSupport =
        Array.isArray(question.supportAids) && question.supportAids.length > 0
      if (!hasSupport) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_SUPPORT_RECOMMENDED',
            `Family "${question.familyId}" should have a support visual aid`,
            {
              field: 'supportAids',
              questionId: qId,
              familyId: fId,
              severity: 50,
              suggestion: visualPolicy.supportDefault
                ? `Add a "${visualPolicy.supportDefault}" visual to supportAids`
                : 'Add an appropriate visual to supportAids',
            },
          ),
        )
      } else if (hasSupport && visualPolicy.supportShowWhen) {
        // ── NEW: Support showWhen validation (C2) ──────────────────────────
        // Validate that support aids have the correct showWhen property
        for (let i = 0; i < question.supportAids.length; i++) {
          const aid = question.supportAids[i] as
            | Record<string, unknown>
            | undefined
          if (aid && typeof aid === 'object') {
            const showWhen = aid.showWhen as string | undefined
            if (!showWhen) {
              issues.push(
                info(
                  'visual',
                  STAGE,
                  'VISUAL_SUPPORT_MISSING_SHOW_WHEN',
                  `Support aid at index ${i} is missing showWhen property — policy expects "${visualPolicy.supportShowWhen}"`,
                  {
                    field: `supportAids[${i}].showWhen`,
                    expected: visualPolicy.supportShowWhen,
                    questionId: qId,
                    familyId: fId,
                    severity: 20,
                    suggestion: `Add showWhen: "${visualPolicy.supportShowWhen}" to the support aid`,
                  },
                ),
              )
            } else if (showWhen !== visualPolicy.supportShowWhen) {
              issues.push(
                info(
                  'visual',
                  STAGE,
                  'VISUAL_SUPPORT_SHOW_WHEN_MISMATCH',
                  `Support aid showWhen "${showWhen}" does not match policy "${visualPolicy.supportShowWhen}" for family "${question.familyId}"`,
                  {
                    field: `supportAids[${i}].showWhen`,
                    expected: visualPolicy.supportShowWhen,
                    actual: showWhen,
                    questionId: qId,
                    familyId: fId,
                    severity: 15,
                  },
                ),
              )
            }
          }
        }
      }
    }
  }

  // ── Semantic convention check ────────────────────────────────────────────
  // [] = reviewed, no visuals needed; omit/undefined = unreviewed

  if (
    question.stimulusAids !== undefined &&
    !Array.isArray(question.stimulusAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_STIMULUS_INVALID_TYPE',
        'stimulusAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'stimulusAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    question.supportAids !== undefined &&
    !Array.isArray(question.supportAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_SUPPORT_INVALID_TYPE',
        'supportAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'supportAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  // ── Answer leakage check ─────────────────────────────────────────────────

  if (
    Array.isArray(question.stimulusAids) &&
    question.stimulusAids.length > 0
  ) {
    const correctAnswer =
      question.questionType === 'multiple_choice' && question.options
        ? question.options[question.correctAnswer ?? -1]
        : undefined

    if (
      correctAnswer &&
      typeof correctAnswer === 'string' &&
      correctAnswer.trim()
    ) {
      const answerLower = correctAnswer.trim().toLowerCase()
      for (let i = 0; i < question.stimulusAids.length; i++) {
        const aid = question.stimulusAids[i]
        if (aid && typeof aid === 'object' && 'data' in aid) {
          const dataStr = JSON.stringify(aid.data).toLowerCase()
          if (
            dataStr.includes(`"label":"${answerLower}"`) ||
            dataStr.includes(`"value":"${answerLower}"`)
          ) {
            issues.push(
              coach(
                'visual',
                STAGE,
                'VISUAL_ANSWER_LEAKAGE',
                `Stimulus visual at index ${i} may leak the correct answer "${correctAnswer}"`,
                {
                  field: `stimulusAids[${i}]`,
                  questionId: qId,
                  familyId: fId,
                  severity: 60,
                  suggestion:
                    'Remove or modify this visual to not reveal the answer, or move it to supportAids',
                },
              ),
            )
          }
        }
      }
    }
  }

  return issues
}
